import { useMemo, useState } from "react";

import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export type HeroComboboxOption = {
  value: string;
  label: string;
};

interface HeroComboboxProps {
  placeholder: string;
  value: string;
  onValueChange: (value: string) => void;
  options: HeroComboboxOption[];
  disabled?: boolean;
  triggerClassName?: string;
  contentClassName?: string;
  ariaLabel?: string;
}

/**
 * Non-modal dropdown (Popover-based) used in the Hero filters.
 * Avoids Radix Select's RemoveScroll scroll-lock behavior to prevent page flicker.
 */
export function HeroCombobox({
  placeholder,
  value,
  onValueChange,
  options,
  disabled,
  triggerClassName,
  contentClassName,
  ariaLabel,
}: HeroComboboxProps) {
  const [open, setOpen] = useState(false);

  const selected = useMemo(
    () => options.find((o) => o.value === value),
    [options, value],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel || placeholder}
          disabled={disabled}
          className={cn("w-full justify-between font-normal", triggerClassName)}
        >
          <span className="truncate">{selected?.label ?? placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="start"
        sideOffset={8}
        avoidCollisions={false}
        className={cn(
          "w-[--radix-popper-anchor-width] p-0 z-[100]",
          contentClassName
        )}
      >
        <Command className="p-1">
          <CommandList>
            <CommandEmpty>No option found.</CommandEmpty>
            <CommandGroup>
              {options.map((opt) => (
                <CommandItem
                  key={opt.value}
                  value={opt.label}
                  onSelect={() => {
                    onValueChange(opt.value);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === opt.value ? "opacity-100" : "opacity-0",
                    )}
                  />
                  {opt.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
